<script lang="ts">
	import { format } from 'svelte-i18n';

	import {
		control_bluetooth_device_ios,
		control_bluetooth_general_ios,
	} from 'src/services/native_container_requests_ios';
	import {
		control_bluetooth_device_android,
		control_bluetooth_general_android,
	} from 'src/services/native_container_requests_android';

	import { app, os } from 'src/stores/session/native-bridge';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { devices } from 'src/stores/lists/devices';
	import { devices_settings } from 'src/stores/device-runtime/status';

	import { Button, Text, Input } from '@Shelly/shelly-design-system';
	import { bulkUpdate } from '../../ApplicationSettings/app_settings_helper';
	import { mathNormalize, valueOutOfRange } from 'src/helpers/utilities';
	import { BLU_STATUS } from 'src/types/requests/settings/blu_status';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { round } from 'src/helpers/units_helpers';
	import {
		getBleTemperatureOffsetRange,
		isBleTemperatureOffsetFahrenheit,
	} from 'src/helpers/ble_settings_helpers';
	import {
		celsius_to_fahrenheit,
		fahrenheit_to_celsius,
		real_id,
	} from 'src/helpers/interface_helpers';

	export let device;
	export let setting: 'offset' | 'threshold';
	export let type: 'temperature' | 'humidity' | 'utc' | 'bright' | 'dark' | 'smoke';
	export let min: number;
	export let max: number;

	let initialized = false;
	let value: any = 0;
	let rawMin = -32768;
	let rawMax = 32767;
	let rawValue = 0;
	let command = '';
	let title = '';
	let param = '';
	let unit = '';
	let ariaLabel = '';
	let step = 1;

	const initDevice = $devices[device?.id] ?? $devices[real_id(device?.id)] ?? device;
	const initSettings = $devices_settings[real_id(initDevice?.id ?? device?.id)];
	$: liveDevice = $devices[device?.id] ?? $devices[real_id(device?.id)] ?? device;
	$: liveSettings = $devices_settings[real_id(liveDevice?.id ?? device?.id)];

	const usesFahrenheit = (d = liveDevice) => isBleTemperatureOffsetFahrenheit(d, liveSettings);

	let prevUnit = isBleTemperatureOffsetFahrenheit(initDevice, initSettings);

	/** Keep the °C/°F chrome in sync with the unit actually used for this device. */
	const applyTemperatureChrome = (useFahrenheit: boolean) => {
		unit = useFahrenheit ? '°F' : '°C';
		({ min, max } = getBleTemperatureOffsetRange(useFahrenheit));
		title = $format('text_sensor_settings_temperature_new', {
			default: 'Offset in {unit} to be applied to the measured temperature.',
			values: { unit },
		});
		ariaLabel = $format('input_temperature_offset', {
			default: 'Enter the temperature offset. Value must be between {min} — {max}!',
			values: { min, max },
		});
	};

	/** Recompute the editable value/rawValue from the given device snapshot. */
	const applyDeviceValue = (d) => {
		if (setting === 'offset') {
			if (type === 'temperature') {
				rawValue = d?.temperatureOffset || 0;
				let next = round(rawValue * step);
				if (usesFahrenheit(d) && Number(next) !== 0) {
					next = round(celsius_to_fahrenheit(Number(next)) - celsius_to_fahrenheit(0));
				}
				value = next;
			} else if (type === 'humidity') {
				rawValue = d?.humidityOffset || 0;
				value = Math.round(rawValue * step);
			} else if (type === 'utc') {
				value = d?.utcOffset || 0;
			}
		} else if (setting === 'threshold') {
			if (type === 'bright') value = d?.brightThreshold || 500;
			else if (type === 'dark') value = d?.darkThreshold || 50;
			else if (type === 'smoke') value = d?.smokeThreshold || 30;
			else if (type === 'temperature') value = d?.temperatureThreshold || 50;
		}
	};

	if (setting === 'offset') {
		if (type === 'temperature') {
			command = 'setTemperatureOffset';
			param = 'temperatureOffset';
			step = 0.1;
			applyTemperatureChrome(prevUnit);
		} else if (type === 'humidity') {
			command = 'setHumidityOffset';
			param = 'humidityOffset';
			rawValue = device?.humidityOffset || 0;
			value = Math.round(rawValue * step);
			unit = '%';
			title = $format('text_sensor_settings_humidity', {
				default: 'Humidity offset in %. Value is applied to measured humidity.',
			});
			ariaLabel = $format('input_humidity_offset', {
				default: 'Enter the humidity offset. Value must be between {min} — {max}!',
				values: { min, max },
			});
		} else if (type === 'utc') {
			command = 'setUtcOffset';
			param = 'utcOffset';
			value = device?.utcOffset || 0;
			unit = $format('timer_text_min', {
				default: 'min',
			});
			title = $format('utc_offset_desctription', {
				default:
					"Set the device's time offset according to its timezone. In rare cases, it may differ from the account's.",
			});
			ariaLabel = $format('input_utc_offset', {
				default: 'Enter the UTC offset. Value must be between {min} — {max}!',
				values: { min, max },
			});
		}
	} else if (setting === 'threshold') {
		if (type === 'bright') {
			command = 'setBrightThreshold';
			param = 'brightThreshold';
			value = device?.brightThreshold || 500;
			title = $format('bright_threshold_description', {
				default: 'Illuminance values above this threshold will be considered "bright"',
			});
			ariaLabel = $format('input_bright_threshold', {
				default: 'Enter the bright threshold. Value must be between {min} — {max}!',
				values: { min, max },
			});
		} else if (type === 'dark') {
			command = 'setDarkThreshold';
			param = 'darkThreshold';
			value = device?.darkThreshold || 50;
			title = $format('dark_threshold_description', {
				default: 'Illuminance values below this threshold will be considered "dark"',
			});
			ariaLabel = $format('input_dark_threshold', {
				default: 'Enter the dark threshold. Value must be between {min} — {max}!',
				values: { min, max },
			});
		} else if (type === 'smoke') {
			command = 'setSmokeThreshold';
			param = 'smokeThreshold';
			value = device?.smokeThreshold || 30;
			title = $format('smoke_threshold_description', {
				default: 'Set smoke sensitivity, in %/m',
			});
			ariaLabel = $format('input_smoke_threshold', {
				default: 'Enter the smoke threshold. Value must be between {min} — {max}!',
				values: { min, max },
			});
		} else if (type === 'temperature') {
			command = 'setTemperatureThreshold';
			param = 'temperatureThreshold';
			value = device?.temperatureThreshold || 50;
			title = $format('temperature_threshold_description', {
				default: 'Set temperature threshold, in °C',
			});
			unit = '°C';
			ariaLabel = $format('input_temperature_threshold', {
				default: 'Enter the temperature threshold. Value must be between {min} — {max}!',
				values: { min, max },
			});
		}
	}

	// Seed the initial value from the live device (step/unit are already set above).
	applyDeviceValue(initDevice);

	let prevStatus;
	$: {
		const status = $controlBleDevice.currentStatusIndex;
		if (status === BLU_STATUS.DEVICE_PROCESSED && status !== prevStatus && liveDevice) {
			applyDeviceValue(liveDevice);
		}
		prevStatus = status;
	}

	const onSave = () => {
		if ($controlBleDevice.connectionState === 2) {
			let realValue = ['temperature', 'humidity'].includes(type) ? rawValue : value;

			if (
				setting === 'offset' &&
				type === 'temperature' &&
				Number(realValue) !== 0 &&
				usesFahrenheit(liveDevice)
			) {
				const celsiusOffset = fahrenheit_to_celsius(value) - fahrenheit_to_celsius(0);
				realValue = Math.round(celsiusOffset / step);
			}

			$os === 'IOS'
				? control_bluetooth_general_ios(device.id, command, param, realValue)
				: control_bluetooth_general_android(device.id, command, param, realValue);

			bulkUpdate(device.id, { [param]: realValue });
		} else {
			$os === 'IOS'
				? control_bluetooth_device_ios(device.id, 'connect')
				: control_bluetooth_device_android(device.id, 'connect');
		}
	};

	$: switch ($controlBleDevice.currentStatusIndex) {
		// after receiving one of the statuses below remove the initialized state
		case BLU_STATUS.UNHANDLED_STATE:
		case BLU_STATUS.BUTTON_PRESS_REQUIRED:
		case BLU_STATUS.CONNECT_TO_DEVICE:
		case BLU_STATUS.DISCONNECT_FROM_DEVICE:
			initialized = false;
			break;
		// after receiving one of the statuses below add the initialized state
		case BLU_STATUS.DEVICE_OTA_AVAILABLE:
		case BLU_STATUS.DEVICE_OTA_REQUIRED:
		case BLU_STATUS.DEVICE_PROCESSED:
		case BLU_STATUS.UTC_OFFSET_SET:
		case BLU_STATUS.TEMPERATURE_OFFSET_SET:
		case BLU_STATUS.HUMIDITY_OFFSET_SET:
		case BLU_STATUS.DARK_THRESHOLD_SET:
		case BLU_STATUS.BRIGHT_THRESHOLD_SET:
		case BLU_STATUS.INVERT_DISPLAY_ENABLED:
		case BLU_STATUS.INVERT_DISPLAY_DISABLED:
		case BLU_STATUS.FAHRENHEIT_ENABLED:
		case BLU_STATUS.FAHRENHEIT_DISABLED:
		case BLU_STATUS.CLOCK_SYNC_ENABLED:
		case BLU_STATUS.CLOCK_SYNC_DISABLED:
		case BLU_STATUS.POWER_SAVER_ENABLED:
		case BLU_STATUS.POWER_SAVER_DISABLED:
		case BLU_STATUS.ZIGBEE_ENABLED:
		case BLU_STATUS.ZIGBEE_DISABLED:
		case BLU_STATUS.SMOKE_THRESHOLD_SET:
		case BLU_STATUS.TEMPERATURE_THRESHOLD_SET:
		case BLU_STATUS.FIRE_GROUP_SET:
		case BLU_STATUS.CLOCK_MODE_SET:
			initialized = true;
			break;
		default:
			break;
	}

	$: if (liveDevice && setting === 'offset' && type === 'temperature') {
		const useFahrenheit = usesFahrenheit(liveDevice);
		if (useFahrenheit !== prevUnit) {
			prevUnit = useFahrenheit;
			applyTemperatureChrome(useFahrenheit);
			applyDeviceValue(liveDevice);
		}
	}

	$: error = valueOutOfRange(value, min, max)
		? $format('error_out_of_range', {
				default: `Value ${value} is out of range. Please provide a value within the limits. (${min} — ${max})`,
				values: {
					value,
					unit,
					min,
					max,
				},
			})
		: '';
</script>

<Text styleType="body-small" class="mb-s">{title}</Text>

<Input
	aria-label={ariaLabel}
	{...getNumberInputConfig()}
	icon="fas fa-calculator"
	class="mb-m"
	{value}
	{step}
	{min}
	{max}
	on:input={(e) => {
		value = e.target['value'];
		if (['temperature', 'humidity'].includes(type)) {
			rawValue = mathNormalize(Math.round(+value / step), rawMin, rawMax);
		}
	}}
	info={$format('min_max_error_description', {
		default: 'Values must be between {min} — {max}.',
		values:
			type === 'temperature'
				? { min: min + unit, max: max + unit }
				: type === 'humidity'
					? { min: min + '%', max: max + '%' }
					: { min, max },
	})}
	{error}
	id="input-i9c9nc2"
/>

<Button disabled={!initialized || !$app || error} size="m" width="full" on:click={onSave}>
	{$format('button_save', { default: 'Save' })}
</Button>
